import Quill from 'quill';
import Block from 'quill/blots/block';
import type { Range } from 'quill/core/selection';
import type Delta from 'quill-delta';

class LoadingImage extends Block {
  static blotName = 'imageBlot';
  static className = 'image-uploading';
  static tagName = 'span';

  static create(src: unknown) {
    const node = super.create(src) as HTMLElement;
    if (src === true) return node;
    const image = document.createElement('img');
    image.setAttribute('src', src as string);
    node.appendChild(image);
    return node;
  }

  deleteAt(index: number, length: number) {
    super.deleteAt(index, length);
    (this as unknown as { cache: Record<string, unknown> }).cache = {};
  }

  static value(domNode: HTMLElement) {
    const { src, custom } = (domNode as HTMLElement & { dataset: DOMStringMap }).dataset;
    return { src, custom };
  }
}

Quill.register({ 'formats/imageBlot': LoadingImage });

export type UploadFn = (file: File) => Promise<string>;

class ImageUploader {
  private quill: Quill;
  private upload: UploadFn;
  private range: Range | null = null;
  private placeholderDelta: Delta | null = null;

  constructor(quill: Quill, options: { upload: UploadFn }) {
    this.quill = quill;
    this.upload = options.upload;

    const toolbar = quill.getModule('toolbar') as { addHandler: (name: string, handler: () => void) => void } | null;
    toolbar?.addHandler('image', this.selectLocalImage.bind(this));

    quill.root.addEventListener('drop', this.handleDrop.bind(this), false);
    quill.root.addEventListener('paste', this.handlePaste.bind(this), false);
  }

  private selectLocalImage() {
    this.quill.focus();
    this.range = this.quill.getSelection();
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.setAttribute('style', 'visibility:hidden');
    input.onchange = () => {
      if (input.files?.[0]) this.readAndUploadFile(input.files[0]);
    };
    document.body.appendChild(input);
    input.click();
    window.requestAnimationFrame(() => document.body.removeChild(input));
  }

  private handleDrop(evt: DragEvent) {
    const file = evt.dataTransfer?.files?.[0];
    if (!file) return;
    evt.stopPropagation();
    evt.preventDefault();
    const selection = document.getSelection();
    const range = document.caretRangeFromPoint(evt.clientX, evt.clientY);
    if (selection && range) {
      selection.setBaseAndExtent(range.startContainer, range.startOffset, range.startContainer, range.startOffset);
    }
    this.quill.focus();
    this.range = this.quill.getSelection();
    setTimeout(() => {
      this.quill.focus();
      this.range = this.quill.getSelection();
      this.readAndUploadFile(file);
    }, 0);
  }

  private handlePaste(evt: ClipboardEvent) {
    const items = evt.clipboardData?.items;
    if (!items) return;
    const IMAGE_MIME = /^image\/(jpe?g|gif|png|svg|webp)$/i;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item || !IMAGE_MIME.test(item.type)) continue;
      const file = item.getAsFile();
      if (file) {
        this.quill.focus();
        this.range = this.quill.getSelection();
        evt.preventDefault();
        setTimeout(() => {
          this.quill.focus();
          this.range = this.quill.getSelection();
          this.readAndUploadFile(file);
        }, 0);
      }
    }
  }

  private readAndUploadFile(file: File) {
    let rejected = false;
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      if (!rejected) this.insertBase64Image(reader.result as string);
    });
    reader.readAsDataURL(file);

    this.upload(file).then(
      url => this.insertToEditor(url),
      err => {
        rejected = true;
        this.removeBase64Image();
        console.warn(err); // eslint-disable-line no-console
      }
    );
  }

  private insertBase64Image(url: string) {
    if (!this.range) return;
    this.placeholderDelta = this.quill.insertEmbed(this.range.index, LoadingImage.blotName, url, 'user');
  }

  private insertToEditor(url: string) {
    if (!this.range || !this.placeholderDelta) return;
    const length = this.placeholderDelta.ops.reduce((n, op) => ('insert' in op ? n + 1 : n), 0);
    this.quill.deleteText(this.range.index, length, 'user');
    this.quill.insertEmbed(this.range.index, 'image', url, 'user');
    this.quill.setSelection({ index: this.range.index + 1, length: 0 }, 'user');
  }

  private removeBase64Image() {
    if (!this.range || !this.placeholderDelta) return;
    const length = this.placeholderDelta.ops.reduce((n, op) => ('insert' in op ? n + 1 : n), 0);
    this.quill.deleteText(this.range.index, length, 'user');
  }
}

export default ImageUploader;
